/**
 * Authentication Context
 * Provides global authentication state and methods to all components
 *
 * Usage:
 * 1. Wrap your app in <AuthProvider>
 * 2. Use const { user, login, logout, ... } = useAuth()
 *
 * State includes:
 * - user: Current authenticated user or null
 * - isLoading: Whether an auth operation is in progress
 * - error: Error message from last failed operation
 * - isAuthenticated: Boolean indicating if user is logged in
 */

'use client' // Next.js directive for client component

import React, { createContext, useContext, useState, useEffect } from 'react'
import api from '@/lib/api'

// Create context
const AuthContext = createContext(null)

/**
 * AuthProvider component
 * Wrap your app with this to provide auth context to all children
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)

  /**
   * Save/clear token in localStorage for cross-port persistence
   */
  const saveToken = (token) => {
    if (typeof window !== 'undefined' && token) {
      localStorage.setItem('auth_token', token)
    }
  }
  const clearToken = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token')
    }
  }
  const getToken = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token')
    }
    return null
  }

  /**
   * Try to restore user session from backend
   * Called on component mount
   */
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const token = getToken()
        if (!token) {
          setUser(null)
          setIsInitialized(true)
          return
        }

        // Try to get current user with stored token
        const response = await api.get('/api/users/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response && response.user) {
          setUser(response.user)
        } else {
          clearToken()
          setUser(null)
        }
      } catch (err) {
        // Invalid token, clear it
        clearToken()
        setUser(null)
      } finally {
        setIsInitialized(true)
      }
    }

    restoreSession()
  }, [])

  /**
   * Register new user
   */
  const register = async (username, email, password, tier = 'free') => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.post('/api/auth/register', {
        username,
        email,
        password,
        tier,
      })

      if (response && response.user) {
        if (response.token) saveToken(response.token)
        setUser(response.user)
        return response
      }

      throw new Error('Registration failed - no user returned')
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Registration failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Login user with credentials
   */
  const login = async (username, password) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.post('/api/auth/login', {
        username,
        password,
      })

      if (response && response.user) {
        if (response.token) saveToken(response.token)
        setUser(response.user)
        return response
      }

      throw new Error('Login failed - no user returned')
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Login failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Logout user
   */
  const logout = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await api.post('/api/auth/logout')
      clearToken()
      setUser(null)
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Logout failed'
      setError(errorMessage)
      clearToken()
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Update user profile (email, password)
   */
  const updateProfile = async (updates) => {
    setIsLoading(true)
    setError(null)

    try {
      if (!user) throw new Error('No authenticated user')

      const response = await api.put(`/api/users/${user.id}`, updates)

      if (response && response.user) {
        setUser(response.user)
        return response
      }

      throw new Error('Profile update failed')
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Profile update failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Refresh user data from server
   */
  const refresh = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Try to post to refresh endpoint if it exists
      const response = await api.post('/api/auth/refresh')
      if (response && response.user) {
        setUser(response.user)
        return response
      }

      throw new Error('Refresh failed')
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Refresh failed'
      setError(errorMessage)

      // If refresh fails (e.g., token expired), clear user
      if (err.shouldRedirectToLogin) {
        setUser(null)
      }

      throw err
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Impersonate a user (admin only)
   * Saves admin token as backup, switches to impersonated user
   */
  const impersonate = async (userId) => {
    setIsLoading(true)
    setError(null)

    try {
      const token = getToken()
      const response = await api.post('/api/auth/impersonate', { userId }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response && response.token && response.user) {
        // Backup current admin token
        if (typeof window !== 'undefined') {
          localStorage.setItem('admin_token_backup', token)
        }

        // Switch to impersonated user
        saveToken(response.token)
        setUser(response.user)
        return response
      }

      throw new Error('Impersonation failed - no token returned')
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Impersonation failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Stop impersonation - restore admin token
   */
  const stopImpersonation = async () => {
    setIsLoading(true)
    setError(null)

    try {
      if (typeof window !== 'undefined') {
        const adminToken = localStorage.getItem('admin_token_backup')
        if (adminToken) {
          // Restore admin token
          saveToken(adminToken)
          localStorage.removeItem('admin_token_backup')

          // Restore admin user data
          const response = await api.get('/api/users/me', {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          })
          if (response && response.user) {
            setUser(response.user)
          }
        }
      }
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Failed to stop impersonation'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Check if currently impersonating
   */
  const isImpersonating = typeof window !== 'undefined'
    ? !!localStorage.getItem('admin_token_backup')
    : false

  /**
   * Register via public flow (with OTP token)
   */
  const registerPublic = async (otpToken, name, password) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.post('/api/auth/register/public', {
        otpToken,
        name,
        password,
      })

      if (response && response.user) {
        if (response.token) saveToken(response.token)
        setUser(response.user)
        return response
      }

      throw new Error('Registration failed - no user returned')
    } catch (err) {
      const errorMessage = err.data?.error || err.message || 'Registration failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Clear error state
   */
  const clearError = () => setError(null)

  // Context value
  const value = {
    // State
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
    isInitialized,
    isImpersonating,

    // Methods
    register,
    registerPublic,
    login,
    logout,
    updateProfile,
    refresh,
    clearError,
    impersonate,
    stopImpersonation,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to use auth context
 * Usage: const { user, login, logout } = useAuth()
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export default AuthContext
